/**
 * Investments List Page — Enterprise Data Table
 *
 * Table: Code, Fund, Investee, Instrument, Amount, Currency, Status, Maturity
 * Create + Edit dialogs
 * Status lifecycle transitions (PATCH /investments/{id}/status)
 * API: GET /api/v1/impact-core/investments/
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Search,
  X,
  ChevronRight,
  Briefcase,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { apiClient } from '@/api/client';
import { StatusBadge } from '@/components/ui/StatusBadge';
import type { Investment, InvestmentStatus, InvestmentCreatePayload, Fund, Investee } from '@/types';

const STATUS_VARIANTS: Record<string, 'default' | 'info' | 'warning' | 'success' | 'muted' | 'danger'> = {
  pipeline: 'default',
  due_diligence: 'info',
  approved: 'warning',
  disbursed: 'info',
  active: 'success',
  restructured: 'warning',
  matured: 'muted',
  written_off: 'danger',
  exited: 'muted',
};

const STATUS_TRANSITIONS: Record<string, InvestmentStatus[]> = {
  pipeline: ['due_diligence'],
  due_diligence: ['approved', 'pipeline'],
  approved: ['disbursed', 'due_diligence'],
  disbursed: ['active'],
  active: ['restructured', 'matured', 'written_off', 'exited'],
  restructured: ['active', 'written_off', 'exited'],
  matured: [],
  written_off: [],
  exited: [],
};

export function InvestmentsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<{ id: number; from: string } | null>(null);

  const investmentsQuery = useQuery<Investment[]>({
    queryKey: ['ic', 'investments'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investments/');
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 30_000,
  });

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const investeesQuery = useQuery<Investee[]>({
    queryKey: ['ic', 'investees'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investees/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const fundMap = useMemo(() => new Map((fundsQuery.data ?? []).map(f => [f.id, f.name])), [fundsQuery.data]);
  const investeeMap = useMemo(() => new Map((investeesQuery.data ?? []).map(i => [i.id, i.name])), [investeesQuery.data]);

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: InvestmentStatus }) => {
      const res = await apiClient.patch(`/api/v1/impact-core/investments/${id}/status`, { status });
      return res.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ic', 'investments'] });
      setTransitionTarget(null);
    },
  });

  const investments = investmentsQuery.data ?? [];

  const filtered = useMemo(() => {
    if (!search) return investments;
    const q = search.toLowerCase();
    return investments.filter(
      (inv) =>
        (inv.investee_name ?? investeeMap.get(inv.investee_id) ?? '').toLowerCase().includes(q) ||
        (inv.investment_code ?? '').toLowerCase().includes(q) ||
        (inv.fund_name ?? fundMap.get(inv.fund_id) ?? '').toLowerCase().includes(q)
    );
  }, [investments, search, fundMap, investeeMap]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-ic-text-primary">Investments</h1>
          <p className="text-xs text-ic-text-tertiary">
            {investments.length} investment{investments.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Investment
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ic-text-tertiary" />
        <input
          type="text"
          placeholder="Search investments..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-ic-border bg-ic-input py-2 pl-9 pr-3 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-ic-border bg-ic-card">
        {investmentsQuery.isLoading ? (
          <TableSkeleton />
        ) : investmentsQuery.isError ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-xs text-red-400">Failed to load investments.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex h-48 flex-col items-center justify-center gap-3">
            <Briefcase className="h-5 w-5 text-ic-text-tertiary" />
            <p className="text-xs text-ic-text-tertiary">
              {investments.length === 0 ? 'No investments yet.' : 'No investments match your search.'}
            </p>
            {investments.length === 0 && (
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Investment
              </button>
            )}
          </div>
        ) : (
          <table className="w-full text-xs ic-table">
            <thead>
              <tr className="border-b border-ic-border text-left text-[10px] uppercase tracking-wider text-ic-text-tertiary">
                <th className="px-4 pb-2 pt-3 font-medium">Code</th>
                <th className="px-4 pb-2 pt-3 font-medium">Fund</th>
                <th className="px-4 pb-2 pt-3 font-medium">Investee</th>
                <th className="px-4 pb-2 pt-3 font-medium">Instrument</th>
                <th className="px-4 pb-2 pt-3 font-medium text-right">Amount</th>
                <th className="px-4 pb-2 pt-3 font-medium">Currency</th>
                <th className="px-4 pb-2 pt-3 font-medium">Status</th>
                <th className="px-4 pb-2 pt-3 font-medium">Maturity</th>
                <th className="px-4 pb-2 pt-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => {
                const transitions = STATUS_TRANSITIONS[inv.status] ?? [];
                return (
                  <tr
                    key={inv.id}
                    className="border-b border-ic-border/50 last:border-0 hover:bg-ic-hover/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/investments/${inv.id}`)}
                  >
                    <td className="py-2.5 px-4 font-mono text-xs text-ic-text-secondary">
                      {inv.investment_code ?? `INV-${inv.id}`}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{inv.fund_name ?? fundMap.get(inv.fund_id) ?? '--'}</td>
                    <td className="py-2.5 px-4 text-xs font-medium text-ic-text-primary">{inv.investee_name ?? investeeMap.get(inv.investee_id) ?? '--'}</td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{inv.instrument_type ?? '--'}</td>
                    <td className="py-2.5 px-4 text-right font-mono text-xs text-ic-text-primary">
                      ${inv.amount.toLocaleString()}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-secondary">{inv.currency}</td>
                    <td className="py-2.5 px-4">
                      <StatusBadge label={inv.status.replace('_', ' ')} variant={STATUS_VARIANTS[inv.status] ?? 'default'} />
                    </td>
                    <td className="py-2.5 px-4 text-xs text-ic-text-tertiary">
                      {inv.maturity_date ? (() => { try { return format(parseISO(inv.maturity_date), 'dd MMM yyyy'); } catch { return inv.maturity_date; } })() : '--'}
                    </td>
                    <td className="py-2.5 px-4" onClick={(e) => e.stopPropagation()}>
                      {transitions.length > 0 && (
                        <div className="relative">
                          <button
                            onClick={() => setTransitionTarget(
                              transitionTarget?.id === inv.id ? null : { id: inv.id, from: inv.status }
                            )}
                            className="text-ic-text-tertiary hover:text-ic-accent transition-colors"
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                          {transitionTarget?.id === inv.id && (
                            <div className="absolute right-0 top-full z-20 mt-1 min-w-[120px] rounded-md border border-ic-border bg-ic-sidebar py-1 shadow-lg">
                              {transitions.map((t) => (
                                <button
                                  key={t}
                                  onClick={() => statusMutation.mutate({ id: inv.id, status: t })}
                                  className="block w-full px-3 py-1.5 text-left text-xs text-ic-text-secondary hover:bg-ic-hover hover:text-ic-text-primary"
                                >
                                  {t.replace('_', ' ')}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Dialog */}
      {showCreate && (
        <InvestmentCreateDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['ic', 'investments'] });
          }}
        />
      )}
    </div>
  );
}

// ─── Create Dialog ─────────────────────────────────────────────

function InvestmentCreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [fundId, setFundId] = useState('');
  const [investeeId, setInvesteeId] = useState('');
  const [code, setCode] = useState('');
  const [instrument, setInstrument] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [maturityDate, setMaturityDate] = useState('');
  const [sector, setSector] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const fundsQuery = useQuery<Fund[]>({
    queryKey: ['ic', 'funds'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/funds/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const investeesQuery = useQuery<Investee[]>({
    queryKey: ['ic', 'investees'],
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/impact-core/investees/');
      return res.data?.data ?? res.data ?? [];
    },
  });

  const mutation = useMutation({
    mutationFn: async (payload: InvestmentCreatePayload) => {
      const res = await apiClient.post('/api/v1/impact-core/investments/', payload);
      return res.data;
    },
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: unknown } } };
      const detail = axiosErr.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ')
        : typeof detail === 'object' && detail !== null
          ? JSON.stringify(detail)
          : (detail as string) ?? 'Failed to create investment.';
      setErrors({ _general: message });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!fundId) { setErrors({ fund_id: 'Select a fund' }); return; }
    if (!investeeId) { setErrors({ investee_id: 'Select an investee' }); return; }
    if (!amount || Number(amount) <= 0) { setErrors({ amount: 'Enter a valid amount' }); return; }

    const payload: InvestmentCreatePayload = {
      fund_id: Number(fundId),
      investee_id: Number(investeeId),
      investment_code: code || undefined,
      instrument_type: instrument || undefined,
      amount: Number(amount),
      currency: currency || 'USD',
      maturity_date: maturityDate || undefined,
      sector: sector || undefined,
      status: 'pipeline',
    };

    mutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">Add Investment</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary"><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">{errors._general}</div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Fund *" error={errors.fund_id}>
              <select value={fundId} onChange={(e) => setFundId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none">
                <option value="">Select fund...</option>
                {(fundsQuery.data ?? []).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </FormField>
            <FormField label="Investee *" error={errors.investee_id}>
              <select value={investeeId} onChange={(e) => setInvesteeId(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none">
                <option value="">Select investee...</option>
                {(investeesQuery.data ?? []).map((inv) => <option key={inv.id} value={inv.id}>{inv.name}</option>)}
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Investment Code">
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="INV-001"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none" />
            </FormField>
            <FormField label="Instrument Type">
              <select value={instrument} onChange={(e) => setInstrument(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none">
                <option value="">Select instrument...</option>
                <option value="senior_loan">Senior Loan</option>
                <option value="subordinated_loan">Subordinated Loan</option>
                <option value="equity">Equity</option>
                <option value="guarantee">Guarantee</option>
                <option value="grant">Grant</option>
                <option value="convertible">Convertible</option>
              </select>
            </FormField>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <FormField label="Amount *" error={errors.amount}>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="1000000"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none" />
            </FormField>
            <FormField label="Currency">
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none">
                {['USD', 'EUR', 'GBP', 'CHF', 'ZAR', 'KES'].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FormField>
            <FormField label="Maturity Date">
              <input type="date" value={maturityDate} onChange={(e) => setMaturityDate(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none" />
            </FormField>
          </div>

          <FormField label="Sector">
            <input type="text" value={sector} onChange={(e) => setSector(e.target.value)} placeholder="e.g. Financial Services"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none" />
          </FormField>
        </form>

        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button onClick={onClose} className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors">Cancel</button>
          <button onClick={handleSubmit} disabled={mutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors">
            {mutation.isPending ? 'Creating...' : 'Create Investment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">{label}</label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-10 animate-pulse rounded bg-slate-700/40" />)}
    </div>
  );
}
